import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { sidemenuService } from "../../utils/services/sidemenu.service";
import { toast } from "react-toastify";
import NumberField from "../number-field";
import AutocompleteWithPagination from "../autocomplete-with-pagination";

const SidemenuModal = ({ open, handleClose, onAddData, selectedSidemenu }) => {
  const [formData, setFormData] = useState({
    title: "",
    icon: "",
    order: 0,
    active: true,
    pkey: "",
    parent: "",
    path: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const pkeyCheckTimerRef = useRef(null);

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = "Title is required";
    }

    if (!formData.pkey.trim()) {
      errors.pkey = "PKey is required";
    }

    if (
      formData.order === null ||
      formData.order === undefined ||
      formData.order === ""
    ) {
      errors.order = "Order is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (pkeyCheckTimerRef.current) clearTimeout(pkeyCheckTimerRef.current);
    if (selectedSidemenu) {
      setFormData({
        title: selectedSidemenu?.title || "",
        icon: selectedSidemenu?.icon || "",
        order: selectedSidemenu?.order || 0,
        active: selectedSidemenu?.active ?? true,
        pkey: selectedSidemenu?.pkey || "",
        parent: selectedSidemenu?.parent || "",
        path: selectedSidemenu?.path || "",
      });
      if (selectedSidemenu?.parent) {
        sidemenuService
          .fetch(`pkey=${encodeURIComponent(selectedSidemenu.parent)}&$limit=1`)
          .then((res) => {
            if (res.data?.length > 0) setSelectedParent(res.data[0]);
          })
          .catch(() => {});
      } else {
        setSelectedParent(null);
      }
    } else {
      initialData();
    }
    return () => {
      if (pkeyCheckTimerRef.current) clearTimeout(pkeyCheckTimerRef.current);
      initialData();
    };
  }, [selectedSidemenu]);

  const initialData = () => {
    setFormData({
      title: "",
      icon: "",
      order: 0,
      active: true,
      pkey: "",
      parent: "",
      path: "",
    });
    setFormErrors({});
    setSelectedParent(null);
  };

  const checkPkeyUniqueness = (pkeyValue) => {
    if (pkeyCheckTimerRef.current) clearTimeout(pkeyCheckTimerRef.current);
    if (!pkeyValue?.trim()) return;
    // Skip check if editing and pkey hasn't changed
    if (selectedSidemenu && pkeyValue.trim() === selectedSidemenu.pkey) {
      setFormErrors((prev) => ({ ...prev, pkey: "" }));
      return;
    }
    pkeyCheckTimerRef.current = setTimeout(async () => {
      try {
        const res = await sidemenuService.fetch(
          1,
          0,
          null,
          `pkey=${encodeURIComponent(pkeyValue.trim())}&$limit=1`,
        );
        if (res.total > 0) {
          setFormErrors((prev) => ({
            ...prev,
            pkey: "PKey already in use",
          }));
        }
      } catch (err) {
        console.error("PKey uniqueness check failed:", err);
      }
    }, 2000);
  };

  const handleInputChange = (field, value) => {
    if (field === "title") {
      const autoPkey = value.toLowerCase().replace(/\s+/g, "-");
      setFormData((prev) => ({
        ...prev,
        title: value,
        pkey: autoPkey,
      }));
      if (formErrors.title || formErrors.pkey) {
        setFormErrors((prev) => ({ ...prev, title: "", pkey: "" }));
      }
      checkPkeyUniqueness(autoPkey);
      return;
    }
    if (field === "pkey") {
      setFormData((prev) => ({ ...prev, pkey: value }));
      setFormErrors((prev) => ({ ...prev, pkey: "" }));
      checkPkeyUniqueness(value);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await sidemenuService.create(formData);
      toast.success("Sidemenu item created successfully");
      onAddData();
      handleClose();
      initialData();
    } catch (error) {
      console.error("Create failed:", error);
      toast.error("Failed to create sidemenu item");
    } finally {
      setLoading(false);
    }
  };

  const handleEditData = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await sidemenuService.edit(
        selectedSidemenu._id,
        formData,
      );
      toast.success("Sidemenu item updated successfully");
      onAddData();
      handleClose();
      initialData();
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update sidemenu item");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          padding: 0,
        },
      }}
    >
      <DialogTitle
        style={{
          backgroundColor: "var(--row-bg-even)",
          padding: "16px 24px",
          fontFamily: "Poppins",
          fontSize: 20,
          fontWeight: 600,
          color: "var(--text-tertiary)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {selectedSidemenu ? "Edit Sidemenu Item" : "Add Sidemenu Item"}
        <IconButton
          onClick={handleClose}
          style={{
            color: "#666666",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider style={{ marginBottom: 10 }} />

      <DialogContent>
        <Grid container spacing={3}>
          {/* Title */}
          <Grid item xs={12} md={6}>
            <CustomTypography
              style={{
                fontSize: 14,
                fontFamily: "Poppins",
                fontWeight: 400,
                color: "var(--text-heading)",
              }}
            >
              Title <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              style={{ backgroundColor: "var(--bg-paper)", marginTop: 8 }}
              size="small"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              fullWidth
              placeholder="Enter title"
              error={!!formErrors.title}
              helperText={formErrors.title}
            />
          </Grid>

          {/* Icon */}
          <Grid item xs={12} md={6}>
            <CustomTypography
              style={{
                fontSize: 14,
                fontFamily: "Poppins",
                fontWeight: 400,
                color: "var(--text-heading)",
              }}
            >
              Icon
            </CustomTypography>
            <TextField
              style={{ backgroundColor: "var(--bg-paper)", marginTop: 8 }}
              size="small"
              value={formData.icon}
              onChange={(e) => handleInputChange("icon", e.target.value)}
              fullWidth
              placeholder="Enter icon name"
              error={!!formErrors.icon}
              helperText={formErrors.icon}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTypography
              style={{
                fontSize: 14,
                fontFamily: "Poppins",
                fontWeight: 400,
                color: "var(--text-heading)",
              }}
            >
              Parent
            </CustomTypography>
            <AutocompleteWithPagination
              selectedItem={selectedParent}
              customLabel="Parent"
              onDataChange={(item) => {
                setSelectedParent(item || null);
                handleInputChange("parent", item ? item.pkey : "");
                handleInputChange("order", item ? item.order + 0.0 : null);
              }}
              width="100%"
              optionKey="pkey"
              apiEndPoint={(limit, skip, search) =>
                sidemenuService.fetch(limit, skip, search)
              }
              placeholder="Select parent menu"
              errorMessage={formErrors.parent}
              hideLabel
            />
          </Grid>

          {/* Order */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
            >
              <CustomTypography
                style={{
                  fontSize: 14,
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  color: "var(--text-heading)",
                }}
              >
                Order <span style={{ color: "red" }}>*</span>
              </CustomTypography>
              <Tooltip
                title="Menu items are sorted by this number, lowest first. Any number is allowed."
                arrow
                placement="top"
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 16, color: "#0288d1", cursor: "help" }}
                />
              </Tooltip>
            </Box>
            <NumberField
              style={{ backgroundColor: "var(--bg-paper)" }}
              size="small"
              value={formData.order}
              onChange={(val) => handleInputChange("order", val)}
              fullWidth
              placeholder="Enter order"
              error={!!formErrors.order}
              helperText={formErrors.order}
              inputProps={{
                step: "any",
              }}
            />
          </Grid>

          {/* PKey */}
          <Grid item xs={12} md={6}>
            <CustomTypography
              style={{
                fontSize: 14,
                fontFamily: "Poppins",
                fontWeight: 400,
                color: "var(--text-heading)",
              }}
            >
              PKey <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              style={{ backgroundColor: "var(--bg-paper)", marginTop: 8 }}
              size="small"
              value={formData.pkey}
              onChange={(e) => handleInputChange("pkey", e.target.value)}
              fullWidth
              placeholder="Enter pkey"
              error={!!formErrors.pkey}
              helperText={formErrors.pkey}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <CustomTypography
              style={{
                fontSize: 14,
                fontFamily: "Poppins",
                fontWeight: 400,
                color: "var(--text-heading)",
              }}
            >
              Path
            </CustomTypography>
            <TextField
              style={{ backgroundColor: "var(--bg-paper)", marginTop: 8 }}
              size="small"
              value={formData.path}
              onChange={(e) => handleInputChange("path", e.target.value)}
              fullWidth
              placeholder="Enter path"
              error={!!formErrors.path}
              helperText={formErrors.path}
            />
          </Grid>

          {/* Active Status */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.active}
                  onChange={(e) =>
                    handleInputChange("active", e.target.checked)
                  }
                  sx={{
                    color: "#0040FF",
                    "&.Mui-checked": {
                      color: "#0040FF",
                    },
                  }}
                />
              }
              label={
                <CustomTypography
                  style={{
                    fontSize: 14,
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    color: "var(--text-heading)",
                  }}
                >
                  Active
                </CustomTypography>
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        style={{
          padding: "16px 24px",
          backgroundColor: "var(--row-bg-even)",
          justifyContent: "space-between",
        }}
      >
        <Button
          style={{
            color: "#007DFF",
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
          }}
          variant="outlined"
          onClick={() => {
            handleClose();
            initialData();
          }}
          color="primary"
        >
          Cancel
        </Button>
        <Button
          onClick={(e) =>
            selectedSidemenu ? handleEditData(e) : handleSave(e)
          }
          color="primary"
          variant="contained"
          disabled={loading}
          style={{
            color: "#FFFFFF",
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            width: 100,
            textTransform: "none",
          }}
        >
          {loading ? "Saving..." : selectedSidemenu ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SidemenuModal;
